/**
 * theme.js — MUI theme configuration for RFOS
 *
 * Maps existing RFOS color palette and typography to MUI theme tokens.
 * Dark sidebar + light content area design preserved.
 */

import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#13B5EA',
      dark: '#0A9FD4',
      light: '#4FC9F0',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1B2A4A',
      dark: '#15213A',
      light: '#243B5E',
    },
    error: {
      main: '#D04A32',
    },
    warning: {
      main: '#A47B1A',
    },
    success: {
      main: '#2E8B57',
    },
    info: {
      main: '#1a7fa3',
    },
    background: {
      default: '#EAECF0',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A2E',
      secondary: '#667085',
      disabled: '#9CA3AF',
    },
    divider: '#DFE1E5',
    // Custom RFOS colors accessible via theme.palette.rfos.*
    rfos: {
      surface2: '#F4F5F7',
      surface3: '#1B2A4A',
      sidebar: '#1B2A4A',
      sidebarBorder: '#15213A',
      accent: '#13B5EA',
      accentHover: '#0A9FD4',
      teal: '#2E7D9B',
      purple: '#7B5EA7',
      gold: '#B8860B',
      redAccent: '#D04A32',
      // Job status colors
      statusIntake: '#A47B1A',
      statusQuoted: '#1a7fa3',
      statusConfirmed: '#7049a8',
      statusInpost: '#1e8b4a',
      statusComplete: '#A47B1A',
      statusInvoiced: '#c43d28',
      statusPaid: '#1e8b4a',
      statusVoid: '#dc2626',
      statusArchived: '#78716C',
    },
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    h1: { fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '0.05em' },
    h2: { fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '0.05em' },
    h3: { fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '0.05em' },
    h4: { fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '0.05em' },
    h5: { fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '0.05em' },
    h6: { fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '0.05em' },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    caption: { fontSize: '0.75rem', color: '#9CA3AF' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#EAECF0',
        },
        '::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '::-webkit-scrollbar-track': {
          background: '#F4F5F7',
        },
        '::-webkit-scrollbar-thumb': {
          background: '#C4C7CC',
          borderRadius: '3px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: '#A0A4AB',
        },
        '::selection': {
          background: 'rgba(19, 181, 234, 0.25)',
          color: '#1A1A2E',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: '1px solid #DFE1E5',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1B2A4A',
          borderRight: '1px solid #15213A',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            color: '#9CA3AF',
            fontWeight: 500,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
          },
        },
      },
    },
  },
});

export default theme;
